import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Search, Trash2, ExternalLink, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAccessToken } from '../../services/tokenStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

async function fetchWebsites() {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/websites`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch websites');
  return res.json();
}

async function deleteWebsite(id: string) {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/websites/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to delete website');
  return res.json();
}

export default function AdminWebsitesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-websites'],
    queryFn: fetchWebsites,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWebsite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-websites'] });
    },
  });

  const websites = data?.data?.websites || [];
  const filteredWebsites = websites.filter(
    (w: any) =>
      w.domain.toLowerCase().includes(search.toLowerCase()) ||
      w.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
      w.user?.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-600 text-gray-300';
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Websites</h1>
          <p className="text-gray-400 mt-1">{websites.length} total websites</p>
        </div>
        <Link
          to="/admin/generate"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
        >
          <Globe size={20} />
          Generate Website
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search websites or users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400"
        />
      </div>

      {/* Websites Grid */}
      {isLoading ? (
        <div className="text-center text-gray-400 py-8">Loading...</div>
      ) : filteredWebsites.length === 0 ? (
        <div className="text-center text-gray-400 py-8">No websites found</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWebsites.map((website: any) => (
            <div
              key={website.id}
              className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden hover:border-gray-600 transition-colors"
            >
              {/* Preview */}
              <div className="h-40 bg-gray-700 flex items-center justify-center">
                {website.screenshotUrl ? (
                  <img
                    src={website.screenshotUrl}
                    alt={website.domain}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Globe className="text-gray-500" size={48} />
                )}
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium truncate">{website.domain}</h3>
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(website.status)}`}>
                    {website.status}
                  </span>
                </div>

                {/* User */}
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                  <User size={14} />
                  <span className="truncate">{website.user?.email || 'No user'}</span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                  <span>{website._count?.crmLeads || 0} leads</span>
                  <span>{website._count?.reviews || 0} reviews</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <a
                    href={website.currentActiveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                  >
                    <ExternalLink size={14} />
                    Visit
                  </a>
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this website?')) {
                        deleteMutation.mutate(website.id);
                      }
                    }}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
